
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, X, Loader2, AlertCircle, RefreshCw, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";

const uploadPartnerLogos = async () => {
  const logosToUpload = [
    { name: 'studiocanal.png', url: '/lovable-uploads/cb7fd8ff-4898-4124-9ecc-4b02ccce3858.png' },
    { name: 'pathe.png', url: '/lovable-uploads/88ef33ef-4322-4a5f-8cfa-31053b7234b7.png' },
    { name: 'tiktok.png', url: '/lovable-uploads/5716bfd2-6af3-4003-b16f-c3fc7040f4f1.png' },
    { name: 'tandem.png', url: '/lovable-uploads/b0acab1f-9941-491e-9125-16fdfb0e6113.png' },
    { name: 'cours_florent.png', url: '/lovable-uploads/318e2a0b-b6e5-41f0-b848-50775a33f7cf.png' },
    { name: 'i7.png', url: '/lovable-uploads/81582fb3-eafd-45ec-b55e-39aff09dba20.png' },
    { name: 'adastra_films.png', url: '/lovable-uploads/7a894876-2456-4238-a50e-c7d3e0f16181.png' },
    { name: 'creator_alliance.png', url: '/lovable-uploads/01f1850c-f1d5-4f59-8d15-1ec17aa27006.png' },
    { name: 'creator_academy.png', url: '/lovable-uploads/44968b99-61eb-4e4e-98e7-7f9e26ca7c75.png' },
  ];

  let uploadedCount = 0;
  let errorCount = 0;

  for (const logo of logosToUpload) {
    try {
      console.log(`Attempting to upload ${logo.name} from ${logo.url}`);
      const response = await fetch(logo.url);
      if (!response.ok) {
        console.error(`Failed to fetch image ${logo.url}: ${response.statusText}`);
        errorCount++;
        continue;
      }
      
      const blob = await response.blob();
      const file = new File([blob], logo.name, { type: blob.type });
      
      const { data, error } = await supabase.storage
        .from('partner-logos')
        .upload(logo.name, file, {
          cacheControl: '3600',
          upsert: true
        });
      
      if (error) {
        console.error(`Error uploading ${logo.name}:`, error);
        errorCount++;
      } else {
        console.log(`Successfully uploaded ${logo.name}`);
        uploadedCount++;
      }
    } catch (error) {
      console.error(`Failed to process ${logo.name}:`, error);
      errorCount++;
    }
  }
  
  if (uploadedCount > 0) {
    toast.success(`${uploadedCount} logos partenaires téléchargés avec succès`);
  }
  
  if (errorCount > 0) {
    toast.error(`Échec du téléchargement pour ${errorCount} logos`);
  }
  
  return { uploadedCount, errorCount };
};

export const PartnerLogoUploader = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logoFiles, setLogoFiles] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitializing, setIsInitializing] = useState(false);
  
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const { data, error } = await supabase.rpc('is_admin_user');
        if (error) throw error;
        setIsAdmin(!!data);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      } finally {
        setIsLoading(false);
      }
    };
    
    const initializeLogos = async () => {
      try {
        setIsInitializing(true);
        
        // Check if partner logos bucket exists and has logos
        const { data, error } = await supabase.storage.from('partner-logos').list();
        
        if (error) {
          console.error('Error checking partner logos bucket:', error);
          return;
        }
        
        // If the bucket is empty or has less than expected logos, upload the defaults
        if (!data || data.length < 6) {
          console.log('Partner logos bucket is empty or incomplete, uploading default logos...');
          await uploadPartnerLogos();
        }
      } catch (error) {
        console.error('Error initializing partner logos:', error);
      } finally {
        setIsInitializing(false);
        loadExistingLogos();
      }
    };
    
    checkAdminStatus();
    initializeLogos();
  }, []);
  
  const loadExistingLogos = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase.storage.from('partner-logos').list();
      if (error) throw error;
      console.log('Existing logos:', data);
      setLogoFiles(data || []);
    } catch (error) {
      console.error('Error loading logos:', error);
      toast.error('Impossible de charger les logos existants');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      
      if (selectedFile.size > 500 * 1024) {
        setError("L'image ne doit pas dépasser 500 Ko");
        return;
      }
      
      setFile(selectedFile);
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setPreview(event.target?.result as string);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Veuillez sélectionner un fichier");
      return;
    }

    setIsUploading(true);
    setError(null);
    
    try {
      const fileExt = file.name.split('.').pop();
      let fileName = file.name.split('.')[0].trim()
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^\w-]/g, '');
      
      fileName = `${fileName}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('partner-logos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });
      
      if (error) throw error;
      
      toast.success('Logo partenaire téléchargé avec succès');
      resetForm();
      setIsOpen(false);
      loadExistingLogos();
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      setError(error.message || "Erreur lors du téléchargement");
      toast.error(`Échec du téléchargement: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleInitializeLogos = async () => {
    setIsInitializing(true);
    try {
      await uploadPartnerLogos();
      loadExistingLogos();
    } catch (error) {
      console.error('Error initializing logos:', error);
    } finally {
      setIsInitializing(false);
    }
  };

  const handleDelete = async (fileName: string) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer ${fileName} ?`)) {
      return;
    }
    
    try {
      const { error } = await supabase.storage
        .from('partner-logos')
        .remove([fileName]);
      
      if (error) throw error;
      
      toast.success('Logo supprimé avec succès');
      loadExistingLogos();
    } catch (error: any) {
      console.error('Error deleting logo:', error);
      toast.error(`Échec de la suppression: ${error.message}`);
    }
  };

  const resetForm = () => {
    setFile(null);
    setPreview(null);
    setError(null);
  };

  if (isLoading) {
    return <div className="flex justify-center p-4"><Loader2 className="animate-spin" /></div>;
  }
  
  if (!isAdmin) {
    return null;
  }

  return (
    <div className="mt-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">Gestion des logos partenaires</h3>
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant="outline" 
            onClick={loadExistingLogos}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          
          <Button 
            size="sm" 
            variant="outline" 
            onClick={handleInitializeLogos}
            disabled={isInitializing}
          >
            <Loader2 className={`h-4 w-4 mr-1 ${isInitializing ? 'animate-spin' : ''}`} />
            Initialiser les logos
          </Button>
          
          <Button size="sm" onClick={() => setIsOpen(true)}>
            <Upload className="h-4 w-4 mr-1" />
            Ajouter un logo
          </Button>
        </div>
      </div>
      
      {logoFiles.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {logoFiles.map((file) => (
            <Card key={file.id} className="p-3 relative group">
              <div className="aspect-square flex items-center justify-center">
                <img 
                  src={supabase.storage.from('partner-logos').getPublicUrl(file.name).data.publicUrl} 
                  alt={file.name}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <p className="text-xs text-center mt-2 truncate">{file.name}</p>
              <Button 
                size="icon" 
                variant="destructive" 
                className="absolute top-2 right-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => handleDelete(file.name)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-muted-foreground mb-4">Aucun logo partenaire trouvé</p>
          <Button onClick={handleInitializeLogos} disabled={isInitializing}>
            {isInitializing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Initialisation...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Initialiser les logos par défaut
              </>
            )}
          </Button>
        </div>
      )}
      
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Ajouter un logo partenaire</DialogTitle>
            <DialogDescription>
              Téléchargez un logo de partenaire pour l'afficher sur la page du challenge.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            {preview ? (
              <div className="relative mx-auto">
                <div className="relative w-full max-w-[300px] mx-auto">
                  <img 
                    src={preview} 
                    alt="Aperçu" 
                    className="mx-auto rounded-lg object-contain max-h-[200px]"
                  />
                  <Button 
                    size="icon" 
                    variant="secondary" 
                    className="absolute top-2 right-2 h-8 w-8 rounded-full"
                    onClick={resetForm}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-secondary rounded-lg p-6 text-center">
                <Input 
                  id="file-upload" 
                  type="file" 
                  accept="image/*" 
                  className="hidden" 
                  onChange={handleFileChange}
                />
                <Label htmlFor="file-upload" className="flex flex-col items-center justify-center cursor-pointer">
                  <Upload className="h-10 w-10 text-muted-foreground mb-2" />
                  <span className="text-sm font-medium">Cliquez pour sélectionner un fichier</span>
                  <span className="text-xs text-muted-foreground mt-1">ou glissez-déposez</span>
                </Label>
              </div>
            )}
            
            <p className="text-sm text-muted-foreground text-center">
              Formats supportés : JPG, PNG, SVG, WebP
            </p>
            <p className="text-sm text-muted-foreground text-center">
              Taille maximale : 500 Ko
            </p>
          </div>
          
          <DialogFooter className="flex justify-between sm:justify-end gap-2">
            <Button variant="outline" onClick={() => setIsOpen(false)}>Annuler</Button>
            <Button 
              onClick={handleUpload} 
              disabled={!file || isUploading}
              className="min-w-[80px]"
            >
              {isUploading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isUploading ? 'Téléchargement...' : 'Télécharger'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnerLogoUploader;
